/**
 * Just Carry On — product page and quick view.
 *
 * The option pills pick a variant; the price, Save chip, stock line, buttons, the URL
 * and the gallery follow. Prices switch straight to the new amount (already formatted by
 * Liquid in the product JSON) — they never count up.
 *
 * The product page's section is wired on load. The quick view wires its own buy box
 * through JustCarryOn.initProduct(root, { quickView: true }), which leaves the page's URL
 * alone. The phone dock's Add to cart is theme.js's: on a product with options it opens
 * the quick add rather than following the page's choice.
 */
(function () {
  'use strict';

  var JCO = (window.JustCarryOn = window.JustCarryOn || {});
  var strings = JCO.strings || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  function fill(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function initSection(section, opts) {
    opts = opts || {};
    var jsonEl = $('[data-product-json]', section);
    if (!jsonEl) return;
    var product;
    try { product = JSON.parse(jsonEl.textContent); } catch (err) { return; }

    var form = $('form[data-product-form]', section);
    var variantInput = form && $('[data-variant-select]', form);
    var fieldsets = $$('[data-option]', section);
    var threshold = product.lowStockThreshold || 0;

    /* ---------------- gallery ---------------- */
    var main = $('[data-main]', section);
    var thumbs = $$('[data-thumb]', section);
    var dots = $$('[data-dot]', section);

    function markMedia(mediaId) {
      thumbs.concat(dots).forEach(function (el) {
        if (String(el.getAttribute('data-media-id')) === String(mediaId)) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    }

    function showMedia(mediaId) {
      if (!main || !mediaId) return;
      var slide = main.querySelector('[data-media-id="' + mediaId + '"]');
      if (!slide) return;
      main.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      markMedia(mediaId);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (event) {
        event.preventDefault();
        showMedia(thumb.getAttribute('data-media-id'));
      });
    });

    if (main && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) markMedia(entry.target.getAttribute('data-media-id'));
        });
      }, { root: main, threshold: [0.6] });
      $$('[data-slide]', main).forEach(function (slide) { io.observe(slide); });
    }

    // Start on the selected variant's image when the page was opened with ?variant=.
    var initial = $('[data-thumb][aria-current="true"]', section);
    if (initial && main) {
      var slide = main.querySelector('[data-media-id="' + initial.getAttribute('data-media-id') + '"]');
      if (slide && slide.offsetLeft) main.scrollLeft = slide.offsetLeft;
    }

    if (!fieldsets.length || !variantInput) return;

    /* ---------------- options → variant ---------------- */
    function selectedOptions() {
      return fieldsets.map(function (fieldset) {
        var checked = $('[data-option-input]:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return product.variants.filter(function (v) {
        return v.options.every(function (value, i) { return value === options[i]; });
      })[0] || null;
    }

    /** Greys out values that make no available variant with the other current choices. */
    function markAvailability(options) {
      fieldsets.forEach(function (fieldset, index) {
        $$('[data-option-input]', fieldset).forEach(function (input) {
          var candidate = options.slice();
          candidate[index] = input.value;
          var match = findVariant(candidate);
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function update() {
      var options = selectedOptions();
      var variant = findVariant(options);

      fieldsets.forEach(function (fieldset, index) {
        var label = $('[data-option-selected]', fieldset);
        if (label && options[index]) label.textContent = '- ' + options[index];
      });
      markAvailability(options);

      var addButton = $('[data-add-button]', section);
      var addLabel = addButton && $('[data-add-label]', addButton);
      var buyNow = $('[data-buy-now]', section);

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = strings.unavailable;
        if (buyNow) buyNow.disabled = true;
        return;
      }

      variantInput.value = variant.id;

      // Price, compare-at and the Save chip switch directly to the new values.
      var price = $('[data-price]', section);
      if (price) price.textContent = variant.price_formatted;
      var compare = $('[data-compare]', section);
      var save = $('[data-save]', section);
      var onSale = variant.compare_at_price > variant.price;
      if (compare) {
        compare.hidden = !onSale;
        var compareValue = $('[data-compare-value]', compare);
        if (compareValue && onSale) compareValue.textContent = variant.compare_formatted;
      }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          var pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          save.textContent = fill(strings.savePercent, { percent: pct });
        }
      }

      // Stock line
      var dot = $('[data-stock-dot]', section);
      var stockText = $('[data-stock-text]', section);
      var status = 'in';
      if (!variant.available) status = 'out';
      else if (threshold > 0 && variant.tracked && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) status = 'low';
      if (dot) {
        dot.classList.toggle('product-template__stock-dot--low', status === 'low');
        dot.classList.toggle('product-template__stock-dot--out', status === 'out');
      }
      if (stockText) {
        stockText.textContent = status === 'out' ? strings.soldOut
          : status === 'low' ? fill(strings.onlyLeft, { count: variant.inventory_quantity })
          : strings.inStock;
      }

      // Buttons
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
      if (buyNow) buyNow.disabled = !variant.available;

      // URL and gallery
      if (!opts.quickView) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media_id) showMedia(variant.featured_media_id);
    }

    fieldsets.forEach(function (fieldset) {
      fieldset.addEventListener('change', update);
    });
    markAvailability(selectedOptions());
  }

  JCO.initProduct = initSection;

  function init() {
    $$('[data-product-section]').forEach(function (section) { initSection(section); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
